import { Flex } from '@/components/Flex';
import { Loader } from '@/components/Loader';
import { APOLLO_CLIENTS } from '@/graphql/client';
import type { OrderAppCtaType } from '@/graphql/orderApp/api';
import { useProductVariantsBusinessDetailsQuery } from '@/graphql/orderApp/api';
import { useShoppingBagStrapsModalQuery } from '@/graphql/saleor/api';
import { STRAP_STANDARD_SIZE_ATTRIBUTE } from '@/integrations/saleor/consts';
import { getMultiselectValue } from '@/lib/attributes';
import { NEXT_PUBLIC_IMAGES_FORMAT, THUMBNAIL_IMAGE_SIZE } from '@/lib/const';
import { getTranslatedName } from '@/lib/i18n';
import { mapEdgesToItems } from '@/lib/maps';
import type { ShoppingBagProductWithCheckoutLineId } from '@/lib/types';
import { useRegionProvider } from '@/providers/RegionProvider';
import type { StrapAddToBagStrap } from '@/views/ShoppingBagView/components/ShoppingBagStrapDrawer';

import addAStrapImage from '@/assets/addAStrapImage.png';

import * as S from './AddStrapButton.styles';
import { AddAStrap } from './messages';

export interface AddStrapButtonProps {
  onClick: () => void;
  orderCtaType?: OrderAppCtaType;
  productVariant?: ShoppingBagProductWithCheckoutLineId;
}

export const AddStrapButton = ({
  onClick,
  orderCtaType,
  productVariant,
}: AddStrapButtonProps) => {
  const { query } = useRegionProvider();
  const compatibleStrapIds = getMultiselectValue(
    productVariant?.product.compatibleStraps,
  );
  const { data, loading: dataLoading } = useShoppingBagStrapsModalQuery({
    fetchPolicy: 'cache-first',
    skip: !compatibleStrapIds.length || !productVariant,
    variables: {
      filter: {
        slugs: compatibleStrapIds,
        attributes: [STRAP_STANDARD_SIZE_ATTRIBUTE],
      },
      thumbnailSize: THUMBNAIL_IMAGE_SIZE,
      imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
      channel: query.channel,
      languageCode: query.languageCode,
    },
  });
  const businessSkus: string[] = [...compatibleStrapIds];
  data?.products?.edges.forEach((item) => {
    businessSkus.push(
      ...getMultiselectValue(item.node.defaultVariant?.product.otherSizesSkus),
    );
  });
  const { data: orderAppData, loading: ctaLoading } =
    useProductVariantsBusinessDetailsQuery({
      context: {
        client: APOLLO_CLIENTS.orderApp,
      },
      fetchPolicy: 'network-only',
      skip: !businessSkus.length || !productVariant,
      variables: { skus: businessSkus, channel: query.channel },
    });
  const straps: StrapAddToBagStrap[] = mapEdgesToItems(data?.products)
    .map((item) => item.defaultVariant)
    .filter((item) => {
      // find cta and other size cta
      const ctaArr = orderAppData?.productVariants?.filter((jtem) => {
        return (
          jtem.sku === item?.sku ||
          getMultiselectValue(item?.product.otherSizesSkus).indexOf(jtem.sku) >
            -1
        );
      });
      let condition = false;
      for (let i = 0; i < (ctaArr?.length || 0); i++) {
        if (ctaArr![i].variantCTA === orderCtaType) {
          condition = true;
          break;
        }
      }
      return condition;
    }) as StrapAddToBagStrap[];

  const productName = getTranslatedName('name', productVariant?.product);

  if (!straps.length) return null;

  return !dataLoading && !ctaLoading ? (
    <S.AddButton onClick={onClick}>
      <Flex>
        <S.PlusIcon />
        <S.ButtonText>{AddAStrap}</S.ButtonText>
      </Flex>
      {addAStrapImage && (
        <S.ButtonImageWrapper>
          <S.ButtonImage
            alt={`${productName}.addAStrapImage`}
            fill
            sizes='10vw'
            src={addAStrapImage}
          />
        </S.ButtonImageWrapper>
      )}
    </S.AddButton>
  ) : (
    <Loader data-test-id='AddButtonLoader' height='fullHeight' />
  );
};
